import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@pantry/ui';
import { useAdminMergeProducts, type AdminProduct } from '@pantry/api-client';
import {
  CubeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface MergeProductsDialogProps {
  products: AdminProduct[];
  onClose: () => void;
  onMerged: () => void;
}

export function MergeProductsDialog({ products, onClose, onMerged }: MergeProductsDialogProps) {
  const { t } = useLingui();
  const [primaryId, setPrimaryId] = useState<string>(products[0]?.id || '');
  const mergeProducts = useAdminMergeProducts();

  const primaryProduct = products.find((p) => p.id === primaryId);
  const duplicateProducts = products.filter((p) => p.id !== primaryId);
  const totalItemsToReassign = duplicateProducts.reduce((sum, p) => sum + p.itemCount, 0);

  const handleMerge = async () => {
    if (!primaryId || duplicateProducts.length === 0) return;

    try {
      const result = await mergeProducts.mutateAsync({
        primaryId,
        duplicateIds: duplicateProducts.map((p) => p.id),
      });
      toast.success(
        t`Merged ${result.mergedCount} products, reassigned ${result.itemsReassigned} items`
      );
      onMerged();
    } catch {
      toast.error(t`Failed to merge products`);
    }
  };

  if (products.length < 2) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              <Trans>Merge Products</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Please select at least 2 products to merge.</Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose}>
              <Trans>Close</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            <Trans>Merge Products</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Select the primary product. All items from duplicates will be reassigned to it.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Primary product selection */}
          <div>
            <p className="text-sm font-medium mb-2">
              <Trans>Select primary product (will be kept):</Trans>
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {products.map((product) => (
                <button
                  key={product.id}
                  onClick={() => setPrimaryId(product.id)}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                    primaryId === product.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border-2',
                      primaryId === product.id
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/50'
                    )}
                  >
                    {primaryId === product.id && (
                      <div className="h-2 w-2 rounded-full bg-primary-foreground" />
                    )}
                  </div>
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="h-10 w-10 rounded-lg object-cover bg-muted"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                      <CubeIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {product.barcode || t`No barcode`} • {product.itemCount} {t`items`}
                    </p>
                  </div>
                  {primaryId === product.id && (
                    <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Merge preview */}
          {primaryProduct && duplicateProducts.length > 0 && (
            <div className="rounded-lg border border-border p-4 space-y-3">
              <p className="text-sm font-medium">
                <Trans>Merge Preview:</Trans>
              </p>

              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap gap-1">
                    {duplicateProducts.map((p) => (
                      <div
                        key={p.id}
                        className="inline-flex items-center gap-1 text-xs bg-muted rounded px-2 py-1"
                      >
                        <CubeIcon className="h-3 w-3" />
                        {p.name.slice(0, 20)}
                        {p.name.length > 20 ? '...' : ''}
                      </div>
                    ))}
                  </div>
                </div>
                <ArrowRightIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded px-2 py-1 font-medium">
                  <CheckCircleIcon className="h-3 w-3" />
                  {primaryProduct.name.slice(0, 20)}
                  {primaryProduct.name.length > 20 ? '...' : ''}
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  <Trans>• {duplicateProducts.length} products will be deleted</Trans>
                </p>
                <p>
                  <Trans>• {totalItemsToReassign} items will be reassigned to primary</Trans>
                </p>
              </div>
            </div>
          )}

          {/* Warning */}
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-amber-800 dark:text-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium mb-1">
                <Trans>This action cannot be undone.</Trans>
              </p>
              <p>
                <Trans>The duplicate products will be permanently deleted.</Trans>
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={handleMerge}
            disabled={mergeProducts.isPending || duplicateProducts.length === 0}
          >
            {mergeProducts.isPending ? (
              <Trans>Merging...</Trans>
            ) : (
              <Trans>Merge {products.length} Products</Trans>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
